import React, { useState } from "react";
import styled from "styled-components";
import { FiClock } from "react-icons/fi";
import {
    Header,
    Card,
    ButtonRow,
    PrimaryButton,
    SecondaryButton,
    Section,
    SectionLabel,
    SectionDesc,
} from "./styles/shared";
import EnableToggleCard from "./components/EnableToggleCard";
import DurationPicker from "./components/DurationPicker";

const EDIT_WINDOW_OPTIONS = [
    { label: "1 day", days: 1 },
    { label: "3 days", days: 3 },
    { label: "7 days", days: 7 },
];

const EditWindowSettings = ({
    deadlineDays,
    deadlineEnabled,
    editEnabled,
    savedEditEnabled,
    onEditEnabledChange,
    savingEditEnabled,
    editWindowDays,
    onEditWindowDaysChange,
    onSaveEditSettings,
    savingEditSettings,
    editSettingsDirty,
    onDiscardEditSettings,
}) => {
    const [showEditWindowAlert, setShowEditWindowAlert] = useState(false);
    const editWindowInvalid = deadlineEnabled && editWindowDays > deadlineDays;

    return (
        <>
            <Header>
                <div>
                    <h2>Edit Settings</h2>
                    <p>Allow students to modify their submitted ratings within a time window.</p>
                </div>
            </Header>

            <EnableToggleCard
                title="Allow rating edits"
                desc="When enabled, students can modify their submitted rating within the set number of days after submission, or until the activity deadline — whichever comes first. When disabled, ratings are locked immediately after submission."
                enabled={editEnabled}
                onChange={onEditEnabledChange}
                disabled={savingEditEnabled}
            />

            {editEnabled && (
                <Card>
                    <Section>
                        <SectionLabel>Edit window</SectionLabel>
                        <SectionDesc>
                            Counted from the time of submission. If the window extends past the activity deadline, edits are only allowed until the deadline.
                        </SectionDesc>
                        <DurationPicker
                            value={editWindowDays}
                            options={EDIT_WINDOW_OPTIONS}
                            onChange={onEditWindowDaysChange}
                        />
                    </Section>
                    <ButtonRow>
                        <SecondaryButton
                            type="button"
                            onClick={onDiscardEditSettings}
                            disabled={!editSettingsDirty || savingEditSettings}
                        >
                            Discard changes
                        </SecondaryButton>
                        <PrimaryButton
                            type="button"
                            onClick={() => {
                                if (editWindowInvalid) {
                                    setShowEditWindowAlert(true);
                                } else {
                                    onSaveEditSettings();
                                }
                            }}
                            disabled={!editSettingsDirty || savingEditSettings}
                            $faded={editWindowInvalid}
                        >
                            <FiClock size={16} />
                            {savingEditSettings ? "Saving…" : savedEditEnabled ? "Save changes" : "Enable & Save"}
                        </PrimaryButton>
                    </ButtonRow>
                </Card>
            )}

            {showEditWindowAlert && (
                <AlertOverlay>
                    <AlertContent>
                        <AlertHeader>
                            <AlertIcon>⚠</AlertIcon>
                            <AlertTitle>Invalid Edit Window</AlertTitle>
                        </AlertHeader>
                        <AlertMessage>
                            The edit window ({editWindowDays} days) cannot exceed the review deadline ({deadlineDays} days). Please reduce the edit window.
                        </AlertMessage>
                        <AlertOkButton type="button" onClick={() => setShowEditWindowAlert(false)}>
                            Got it
                        </AlertOkButton>
                    </AlertContent>
                </AlertOverlay>
            )}
        </>
    );
};

export default EditWindowSettings;

const AlertOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(4px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
`;

const AlertContent = styled.div`
    background: white;
    border-radius: 1.5rem;
    padding: 2rem;
    width: 90%;
    max-width: 26rem;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    text-align: center;
`;

const AlertHeader = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
`;

const AlertIcon = styled.span`
    font-size: 2rem;
    line-height: 1;
`;

const AlertTitle = styled.h3`
    font-size: 1.1rem;
    font-weight: 700;
    color: #111827;
    margin: 0;
`;

const AlertMessage = styled.p`
    font-size: 0.9rem;
    color: #4b5563;
    line-height: 1.5;
    margin: 0;
`;

const AlertOkButton = styled.button`
    align-self: center;
    margin-top: 4px;
    padding: 10px 24px;
    border-radius: 12px;
    background: #9333ea;
    color: white;
    font-weight: 700;
    font-size: 14px;
    border: none;
    cursor: pointer;
    &:hover {
        filter: brightness(1.05);
    }
`;
